import type { CmsProduct } from '../../types/cmsProduct';
import type { VerticalType } from '../../types/cmsTypes';
import { getApplyButtonLabel } from '../../utils/helpers/router';
import { tr } from '../../utils/translations/tr';
import { compareCtaLabel, compareCtaTone } from './compareCtaLabel';
import type { CompareCtaTone } from './types';

/**
 * How a vertical's comparison reads and is laid out. Travel insurance compares plans; credit card
 * compares cards, with its own header, column content and copy (BRD-02). Everything that differs
 * between the two is answered here, once, so the components ask "which variant" and never "which
 * vertical".
 */
export type CompareVariant = 'plans' | 'cards';

/**
 * Which verticals compare, and how. Total over the verticals: a new one does not compile until it
 * says whether it compares, so none falls into another's layout and copy by default. `null` means
 * the vertical has no comparison, whatever config an editor creates for it.
 */
export const COMPARE_VARIANT_BY_VERTICAL: Record<
  VerticalType,
  CompareVariant | null
> = {
  TRAVEL_INSURANCE: 'plans',
  CREDIT_CARD: 'cards',
  PERSONAL_LOANS: null,
  CAR_INSURANCE: null,
  INSURANCE: null,
  BANKING: null,
  INVESTMENTS: null,
  TELCO: null,
  PERSONAL_FINANCE: null,
  OTHERS: null,
  HOME_LOAN: null,
  E_WALLET: null,
  LIFESTYLE: null,
  GOVERNMENT_SERVICES: null,
  MONEY_MANAGEMENT: null,
  SPONSORED: null,
  ASSET_MANAGEMENT: null,
  HEALTH_INSURANCE: null,
  PET_INSURANCE: null,
  HOME_INSURANCE: null,
  MAID_INSURANCE: null,
  MOTORCYCLE_INSURANCE: null,
  SME: null,
  OFW: null,
  LIFE_INSURANCE: null,
};

/** The vertical's variant; `undefined` where the vertical does not compare. */
export const compareVariantOf = (
  vertical: VerticalType | undefined,
): CompareVariant | undefined =>
  (vertical && COMPARE_VARIANT_BY_VERTICAL[vertical]) || undefined;

/** Whether the vertical's comparison sends GA events: its variant's `tracked`, the one door for hosts. */
export const compareTrackedFor = (
  vertical: VerticalType | undefined,
): boolean => {
  const variant = compareVariantOf(vertical);
  return !!variant && COMPARE_VARIANT_LAYOUT[variant].tracked;
};

/**
 * The curated credit card set, as cms-service's result page defines it (`CREDIT_CARD_CURATED_TAG`):
 * a card without this category is not on the results page and is not offered for comparison.
 */
export const CREDIT_CARD_CURATED_CATEGORY = 'category:all-credit-cards';

export type CompareVariantLayout = {
  /**
   * Where "+ Add Plan" finds its candidates in the catalogue: the plans of the trip's type (then
   * narrowed to the ones the quote priced), or every curated card of the market.
   */
  candidates: 'tripType' | 'curatedCards';
  /**
   * The results page, as a segment under the vertical's path: travel insurance lists its plans on
   * the vertical's own page, credit card on `/all` (the vertical's page is a hub that lists none).
   * The comparison's way back when the link names no page of its own.
   */
  resultsSegment: string;
  /**
   * Whether the comparison prices a trip the reader searched: the page runs the quote poll, reads
   * the trip from the URL and offers only priced plans, and the results page offers comparison
   * only where it carries the search.
   */
  pricesTrip: boolean;
  /**
   * The compare control a product card carries: the footer row (TI), or the pill the card places
   * itself (credit card). A card renders only its variant's control, never the other one.
   */
  cardControl: 'row' | 'pill';
  /** "+ Add" in the page header, from 1280, while below the cap. */
  headerAdd: boolean;
  /** A count line under the title ("Comparing 2 cards (up to 3)"). */
  countLine: boolean;
  /** The column's picture: the insurer's logo, or the product's own image (the card art). */
  columnImage: 'provider' | 'product';
  /** The issuer's name under the product name. */
  issuerLine: boolean;
  /** Dashed add slots while below the cap: one, or one for every free place. */
  addSlots: 'one' | 'all';
  /** Empty tiles in the tray: one per free place, or a single one hinting at more. */
  trayEmptySlots: 'one' | 'all';
  /** The tray's CTA: the plain "Compare", or a counted one in the CTA colour. */
  trayCta: 'plain' | 'counted';
  /** Whether the variant has a GA tracking plan: the host passes `CompareTracking` only then. */
  tracked: boolean;
};

/** Total over the variants: a new one does not compile until it says how it lays out. */
export const COMPARE_VARIANT_LAYOUT: Record<
  CompareVariant,
  CompareVariantLayout
> = {
  plans: {
    candidates: 'tripType',
    resultsSegment: '',
    pricesTrip: true,
    cardControl: 'row',
    headerAdd: false,
    countLine: false,
    columnImage: 'provider',
    issuerLine: false,
    addSlots: 'one',
    trayEmptySlots: 'all',
    trayCta: 'plain',
    tracked: true,
  },
  cards: {
    candidates: 'curatedCards',
    resultsSegment: 'all',
    pricesTrip: false,
    cardControl: 'pill',
    headerAdd: true,
    countLine: true,
    columnImage: 'product',
    issuerLine: true,
    addSlots: 'all',
    trayEmptySlots: 'one',
    trayCta: 'counted',
    // The credit card tracking plan has not been built yet.
    tracked: false,
  },
};

/**
 * The picture a column, a tray tile or a selector row shows for a product. A card without its own
 * image falls back to the issuer's logo, so a column is never blank where another one has a picture.
 */
export function compareImageOf(
  product: CmsProduct | undefined,
  columnImage: CompareVariantLayout['columnImage'],
): { src: string; isProductImage: boolean } | undefined {
  const own = columnImage === 'product' ? product?.product_image : undefined;
  if (own) return { src: own, isProductImage: true };
  const logo = product?.provider?.provider_image;
  return logo ? { src: logo, isProductImage: false } : undefined;
}

export type CompareVariantCopy = {
  pageTitle: string;
  /** The breadcrumb crumb back to the results page, e.g. "Travel Insurance". */
  backLabel: string;
  /** The header button's label. */
  addLabel: string;
  /** The dashed empty column's label. */
  slotLabel: string;
  /** The selector's title, and its search field's placeholder. */
  chooseTitle: string;
  searchPlaceholder: string;
  emptyTitle: string;
  emptyAction: string;
  noMatchTitle: string;
  /** A second line under the no-match title; the cards copy has none. */
  noMatchHint?: string;
  countLine: (count: number, cap: number) => string;
  /** The hint on a control the cap has disabled. */
  atCap: (cap: number) => string;
  /** The tray CTA when comparing is possible, and while more items are needed. */
  trayCta: (count: number) => string;
  trayCtaDisabled: (missing: number) => string;
};

/** The variant's copy, from the Compare block's translations. */
export function compareVariantCopy(
  variant: CompareVariant,
  t: Record<string, string>,
): CompareVariantCopy {
  switch (variant) {
    case 'plans':
      return {
        pageTitle: t.compare_page_title,
        backLabel: t.compare_results_travel_insurance,
        addLabel: t.compare_add_plan,
        slotLabel: t.compare_add_plan,
        chooseTitle: t.compare_choose_plans,
        searchPlaceholder: t.compare_search_placeholder,
        emptyTitle: t.compare_empty_title,
        emptyAction: t.compare_empty_action,
        noMatchTitle: t.compare_no_plans_found,
        noMatchHint: t.compare_try_another_name,
        countLine: () => '',
        atCap: (cap) => tr('compare_at_cap', t, { cap: String(cap) }),
        trayCta: () => t.compare,
        trayCtaDisabled: () => t.compare,
      };
    case 'cards':
      return {
        pageTitle: t.compare_page_title_cards,
        backLabel: t.compare_results_credit_card,
        addLabel: t.compare_add_card,
        slotLabel: t.compare_add_card_slot,
        chooseTitle: t.compare_choose_cards,
        searchPlaceholder: t.compare_search_placeholder_cards,
        emptyTitle: t.compare_empty_title_cards,
        emptyAction: t.compare_empty_action_cards,
        noMatchTitle: t.compare_no_cards_found,
        countLine: (count, cap) =>
          tr(
            'compare_count_cards',
            t,
            { count: String(count), cap: String(cap) },
            count,
          ),
        atCap: (cap) => tr('compare_at_cap_cards', t, { cap: String(cap) }),
        trayCta: (count) =>
          tr('compare_cta_count_cards', t, { count: String(count) }),
        trayCtaDisabled: (missing) =>
          tr('compare_cta_more_cards', t, { count: String(missing) }),
      };
    default:
      return variant satisfies never;
  }
}

/**
 * The column's call to action. A plan reads its checkout path: "Apply Now" in the CTA colour for an
 * end-to-end checkout, "Buy Now" for a click-out. A card reads as its results card does: the
 * editor's label, else the default.
 *
 * @param compareText the Compare block's translations
 * @param baseText the site's base translations, for the default "Apply Now"
 */
export function compareCta(
  variant: CompareVariant,
  product: CmsProduct,
  compareText: Record<string, string>,
  baseText: Record<string, string>,
): { label: string; tone: CompareCtaTone } {
  switch (variant) {
    case 'plans':
      return {
        label: compareCtaLabel(product, compareText),
        tone: compareCtaTone(product),
      };
    case 'cards':
      return {
        label: getApplyButtonLabel(
          tr('apply_now', baseText),
          undefined,
          product.call_to_action,
        ),
        tone: 'primary',
      };
    default:
      return variant satisfies never;
  }
}
